import { useEffect, useRef, useState } from "react";

import Button, { SIZE } from "components/Button";
import UserInfo from "components/UserInfo";
import profilePhotoGray from "../../../../assets/images/profile_photo_gray.svg";
import Timer from "../Timer";
import quoteService from "services/quote.service";
import Loader from "../Loader";
import EmptyState from "../EmptyState";

import styles from "./RfqTable.module.scss";
import TextWithTooltip from "../TextWithTooltip/TextWithTooltip";
import { formatDate } from "utils/modifyDate";


export const RfqTable = ({ data, loading, handleUseFilters }) => {
  const [rfqList, setRfqList] = useState([]);
  const [showPopupId, setShowPopupId] = useState(null);
  const [newRows, setNewRows] = useState(new Set());
  const [changedRows, setChangedRows] = useState(new Set());
  const prevRfqListRef = useRef(rfqList);

  useEffect(() => {
    if (data && data.length >= 0) {
      const newRowsSet = new Set();
      const changedRowsSet = new Set();
      const currentIds = new Set(data.map((item) => item.id));
      const previousIds = new Set(prevRfqListRef.current.map((item) => item.id));

      currentIds.forEach((id) => {
        if (!previousIds.has(id)) {
          newRowsSet.add(id);
        }
      });

      data.forEach((item) => {
        const prevItem = prevRfqListRef.current.find((prev) => prev.id === item.id);
        if (prevItem && prevItem.status.type !== item.status.type) {
          changedRowsSet.add(item.id);
        }
      });

      setNewRows(newRowsSet);
      setChangedRows(changedRowsSet);

      setRfqList(data);
      prevRfqListRef.current = data;
    }
  }, [data]);

  const theadNames = [
    "Age",
    "Score",
    "Company Info",
    "Miles",
    "Pick-Up",
    "Delivery",
    "Load Info",
    "Notes",
    "Trucks",
    "Source",
    "Order#",
    "Status",
    "View",
    "Take",
  ];

  const redirect = (id, take = false) => {
    let state = {"dispatched": handleUseFilters()}
    localStorage.setItem("rfqID", id);
    localStorage.setItem("shouldTake", JSON.stringify(take));
    
    const query = new URLSearchParams();

    if (state?.dispatched !== undefined) {
      query.set("dispatched", state.dispatched);
    }
  
    const url = `/request_for_quote_page?${query.toString()}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <section className={styles.sectionContainer}>
        <div className={styles.contentContainer}>
          <Loader />
        </div>
      </section>
    );
  }

  if (!loading && (!rfqList || rfqList.length === 0)) {
    return (
      <section className={styles.sectionContainer}>
        <div className={styles.contentContainer}>
          <EmptyState />
        </div>
      </section>
    );
  }

  return (
    <>
      <section className={styles.sectionContainer}>
        <div className={styles.contentContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                {theadNames.map((thName, index) => (
                  <th key={index}>{thName}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rfqList.map(
                ({
                  age,
                  score,
                  company_info,
                  delivery,
                  id,
                  miles,
                  notes,
                  order_number,
                  pick_up,
                  status,
                  trucks_count,
                  source_repr,
                  load_info,
                }) => (
                  <tr
                    key={id}
                    className={`${newRows.has(id) ? styles.newRow : ""} ${
                      changedRows.has(id) ? styles.changedRow : ""
                    }`}
                  >
                    <td>
                      <Timer initialTime={age} />
                    </td>
                    <td>{score}</td>
                    <td>
                      <div className={styles.cellWrap}>
                        <TextWithTooltip
                          text={company_info.name}
                          tooltipStyle={{
                            bottom: "2,7rem",
                            right: "auto",
                            left: "0rem",
                          }}
                        />
                        <p>
                          <TextWithTooltip
                            text={`${company_info.email || ""} ${company_info.phone ? `| ${company_info.phone}` : ""}`}
                            tooltipStyle={{
                              bottom: "2,7rem",
                              right: "auto",
                              left: "0rem",
                            }}
                          />
                        </p>
                      </div>
                    </td>
                    <td>{miles} mi</td>
                    <td>
                      <div className={styles.cellWrap}>
                        <TextWithTooltip
                          text={`${pick_up.city || ""}, ${pick_up.state || ""} ${
                            pick_up.zip || ""
                          }`}
                        />
                        <p>
                          <TextWithTooltip text={`${formatDate(pick_up.date || "")}`} />
                        </p>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellWrap}>
                        <TextWithTooltip
                          text={`${delivery.city || ""}, ${delivery.state || ""} ${
                            delivery.zip || ""
                          }`}
                        />
                        <p>
                          <TextWithTooltip text={`${formatDate(delivery.date)}`} />
                        </p>
                      </div>
                    </td>
                    <td>
                      <div className={styles.cellWrap}>
                        <TextWithTooltip
                          text={load_info?.suggested_truck_size || ""}
                        />
                        <p>
                          <TextWithTooltip
                            text={`${load_info?.pieces || ""} pcs`}
                          />
                          <TextWithTooltip
                            text={`${load_info?.dimensions || ""} | ${load_info?.weight || ""}`}
                          />
                        </p>
                      </div>
                    </td>
                    <td>
                      <TextWithTooltip text={notes} />
                    </td>
                    <td>{trucks_count}</td>
                    <td>{source_repr}</td>
                    <td>
                      <TextWithTooltip text={order_number} />
                    </td>
                    <td
                      onMouseEnter={() => {
                        if (status.type !== "New") setShowPopupId(id);
                      }}
                      onMouseLeave={() => setShowPopupId(null)}
                    >
                      <div className={styles.statusCell}>
                        {" "}
                        {status.type === "New" ? (
                          status.type
                        ) : (
                          <div className={styles.statusCell}>
                            {status.type}{" "}
                            <UserInfo
                              src={status.photo ?? profilePhotoGray}
                              className={styles.userInfo}
                            />
                            {showPopupId === id && (
                              <div className={styles.popup}>
                                <p>
                                  {status.type} <span>{status.action_datetime}</span> by{" "}
                                  <span>{status.user}</span>
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <Button
                        size={SIZE.SMALL}
                        className={styles.viewButton}
                        onClick={() => {
                          redirect(id)}}
                      >
                        View
                      </Button>
                    </td>
                    <td>
                      <Button
                        size={SIZE.SMALL}
                        className={styles.takeButton}
                        onClick={() => {
                          quoteService.sendMsg(
                            `${(pick_up.state || "").toLowerCase()} ${(delivery.state || "").toLowerCase()}`,
                          );
                          redirect(id, true);
                        }}
                      >
                        Take
                      </Button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
};
